<template>
    <div class="post-code-container">
        <div class="left-tab" v-show="showLeftTab">
            <h2>
                {{ model.translations['registerTree.Form.PostCodeLeftTabHeader'] }}
                {{ isCollectionOpen
                    ? model.translations['registerTree.Form.PostCodeLeftTabCollectionOpenInfo']
                    : model.translations['registerTree.Form.PostCodeLeftTabCollectionClosedInfo']
                }}
            </h2>
            <div class="statistics">
                <div v-for="statistic in model.statistics" class="statistics-item">
                    <div class="statistics-item-wrapper">
                        <img :src="statistic.icon" class="statistics-item-image" :alt="statistic.label"
                            :title="statistic.value" />
                        <span class="statistics-item-value">{{ statistic.value }}</span>
                    </div>
                    <span class="statistics-item-label">{{ statistic.label }}</span>
                </div>
            </div>
            <Button as-child variant="default" size="lg" class="mt-4 mp-2">
                <a :href="model.submitButton.url">{{ model.submitButton.text }}</a>
            </Button>
            <div class="explnanatory">{{ model.translations['registerTree.Form.PostCodeLeftTabExplanatoryText'] }}</div>
        </div>
        <div class="right-tab">
            <div class="header">
                <h2>{{ model.translations['registerTree.Form.PostCodeRightTabHeader'] }}</h2>
                <span class="small-text">{{ model.translations['registerTree.Form.PostCodeRightTabInfo'] }}</span>
            </div>
            <form @submit.prevent="onSubmit" class="postcode-form" id="postcode-form">
                <FormField v-slot="{ componentField }" name="postcode">
                    <FormItem class="form-item">
                        <FormControl>
                            <div class="input-button">
                                <Input type="text"
                                    :placeholder="model.translations['registerTree.Form.PostCodeRightTabInputPlaceholder']"
                                    v-bind="componentField" id="post-code-input"></Input>
                                <Button type="submit">
                                    <CircleArrowRight />
                                </Button>
                            </div>
                            <FormMessage />
                        </FormControl>
                    </FormItem>
                </FormField>
            </form>
        </div>
    </div>

</template>
<script lang="ts" setup>
import { computed, inject, onMounted } from 'vue';
import type { IPostCodeFormProps } from './PostCodeForm.model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { CircleArrowRight } from "lucide-vue-next"
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';
import { POST_CODE_REGEX } from '@/contants/formFieldsRegex.constant';
import { useMutation } from '@tanstack/vue-query';
import { findPostCode } from '@/api/registerTree/registerTree.api';
import type { IFindPostCodeRequest } from '@/api/registerTree';
import { PostCodeDataKey } from '../TreeRegistration.model';
import { DEFAULT_POST_CODE_DATA } from '../TreeRegistration.constant';
import { useBreakpoints } from '@vueuse/core';

const breakpoints = useBreakpoints({
    mobile: 0,
    desktop: 768
});
const { model } = defineProps<IPostCodeFormProps>()
const collectionLookup = inject(PostCodeDataKey, DEFAULT_POST_CODE_DATA)

onMounted(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const postcode = urlParams.get("inputValue");

    if (!postcode) return;

    const url = new URL(window.location.href).pathname.replace(/\/[^\/]*$/, '/');
    window.history.replaceState(null, "", url);

    form.setFieldValue('postcode', postcode);
    form.validateField('postcode').then(() => {
        onSubmit();
    });
})

const formSchema = toTypedSchema(z.object({
    postcode: z.string().regex(POST_CODE_REGEX, model.translations["registerTree.Form.ErrorMessages.InvalidPostcode"]).default(''),
}))

const form = useForm({
    validationSchema: formSchema,
})

const { mutate: registerVolunteer } = useMutation({
    mutationFn: (data: IFindPostCodeRequest) => findPostCode(model.submitAction ?? "", data),
    onSuccess: (data, { postCode }) => {
        collectionLookup.collectionLookupResult.value = data.collectionLookupResult;
        collectionLookup.hospices.value = data.hospices;
        collectionLookup.postCode.value = postCode;
    },
})

const onSubmit = form.handleSubmit(({ postcode }) => {
    const standarizedPostcode = postcode.replace(/(\S*)\s*(\d)/, "$1 $2").toUpperCase()
    if (!window.__STATE__) {
        return;
    }
    registerVolunteer({ postCode: standarizedPostcode, culture: window.__STATE__.culture, currentUmbracoPageId: window.__STATE__.currentPageId })
})

const isCollectionOpen = computed(() => {
    const from = new Date(model.collectionStartDate)
    const to = new Date(model.collectionEndDate)
    const now = new Date()

    return from <= now && now <= to
})

const showLeftTab = computed(() => {
    return (!collectionLookup.collectionLookupResult.value && breakpoints.active().value === "mobile") || breakpoints.active().value === "desktop";
})
</script>
<style lang="scss">
.post-code-container {
    display: flex;
    flex-direction: column-reverse;
}

.left-tab,
.right-tab {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;
}

h2 {
    text-align: center;
}

.right-tab {
    gap: 12px;
    text-align: center;

    .postcode-form {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    .small-text {
        font-size: .75rem;
        font-weight: 400;
    }

    .form-item {
        height: 80px;
    }

    .input-button {
        display: flex;
        align-items: center;
        width: 250px;
        height: 50px;

        input {
            height: 100%;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        }

        button {
            height: 100%;
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }
    }
}

.left-tab {
    gap: 20px;

    .explnanatory {
        font-size: 0.75rem;
        color: var(--secondary-foreground);
        font-style: italic;
        align-self: flex-start;
    }

    .statistics {
        display: flex;
        flex-direction: column;
        gap: 20px;
        font-weight: 700;

        .statistics-item {
            display: flex;
            flex-direction: column;
            font-weight: 700;


            .statistics-item-wrapper {
                display: flex;
                align-items: center;
                gap: 5px;
            }

            .statistics-item-image {
                max-width: 40px;
                aspect-ratio: 1;
                object-fit: contain;
            }

            .statistics-item-value {
                color: var(--primary);
                font-size: 1.75rem;
            }

            .statistics-item-label {
                font-size: 1.0625rem;
            }
        }
    }
}

@media (min-width: 768px) {
    .post-code-container {
        display: grid;
        grid-template-columns: 1fr 1fr;
        padding: 20px;
    }

    .left-tab {
        .statistics {
            flex-direction: row;
        }
    }
}
</style>